import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useState } from 'react'
import { useUser } from '../../../contexts/UserContext'
import { truckService } from '../../../services/truckService'
import OnboardingFooter from '../OnboardingFooter'
import OnboardingHeader from '../OnboardingHeader'
import OnboardingInput from '../OnboardingInput'

interface OnboardingStepTruckingInfoProps {
  onNext: () => void
  onBack: () => void
}

interface FormData {
  truckNumber: string
  truckType: string
  licenseNumber: string
  yardAddress: string
}

const OnboardingStepTruckingInfo: React.FC<OnboardingStepTruckingInfoProps> = ({
  onNext,
  onBack,
}) => {
  const { user } = useUser()
  const [formData, setFormData] = useState<FormData>({
    truckNumber: '',
    truckType: '',
    licenseNumber: '',
    yardAddress: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInputChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    }

  const handleSubmit = async () => {
    if (!user) return

    try {
      setIsSubmitting(true)
      setError(null)

      // Add truck information
      await truckService.addTruck(user.id, {
        truck_number: formData.truckNumber,
        truck_type: formData.truckType,
        license_number: formData.licenseNumber,
        yard_address: formData.yardAddress,
      })

      onNext()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save truck information',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={css(styles.root)}>
      <div className={css(styles.content)}>
        <OnboardingHeader title="Trucking Information" progress={66} />
        <div className={css(styles.profilePhotoSection)}>
          <div className={css(styles.profilePhotoCircle)}>
            <div className={css(styles.profilePhotoInner)}>
              <img
                src="/company_icon.png"
                alt="Company Logo"
                style={{ width: 40, height: 40 }}
              />
              <div className={css(styles.profilePhotoLabel)}>Company Logo</div>
            </div>
            <span className={css(styles.addPhotoIcon)}>
              <span className={css(styles.plusNudge)}>+</span>
            </span>
          </div>
        </div>
        <div className={css(styles.formGroup)}>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Truck Number"
              value={formData.truckNumber}
              onChange={handleInputChange('truckNumber')}
              placeholder="Enter your truck number"
              name="truckNumber"
            />
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Truck Type"
              value={formData.truckType}
              onChange={handleInputChange('truckType')}
              placeholder="Enter your truck type"
              name="truckType"
            />
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="License Number"
              value={formData.licenseNumber}
              onChange={handleInputChange('licenseNumber')}
              placeholder="Enter your license number"
              name="licenseNumber"
            />
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Yard Address"
              value={formData.yardAddress}
              onChange={handleInputChange('yardAddress')}
              placeholder="Enter your yard address"
              name="yardAddress"
            />
          </div>
        </div>
        {error && <div className={css(styles.error)}>{error}</div>}
      </div>
      <OnboardingFooter
        onNext={handleSubmit}
        onBack={onBack}
        nextText={isSubmitting ? 'Saving...' : 'Next'}
      />
    </div>
  )
}

const styles = StyleSheet.create({
  title: {
    fontSize: 'clamp(22px, 3vw, 32px)',
    fontWeight: 600,
    color: '#1F3A8A',
    margin: 'clamp(12px, 2vw, 24px) 0 clamp(6px, 1vw, 16px) 0',
    textAlign: 'center',
  },
  progressBarContainer: {
    width: '100%',
    height: 8,
    background: '#E5E7EB',
    borderRadius: 8,
    marginBottom: 24,
    marginTop: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 8,
    background: '#2563EB',
    transition: 'width 0.3s',
  },
  progressBarStep2: {
    width: '66%',
  },
  profilePhotoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 'clamp(16px, 3vw, 32px)',
    position: 'relative',
    width: '100%',
    maxWidth: 'min(100%, 340px)',
    alignSelf: 'center',
  },
  profilePhotoCircle: {
    width: 'clamp(80px, 12vw, 120px)',
    height: 'clamp(80px, 12vw, 120px)',
    borderRadius: '50%',
    background: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
  },
  profilePhotoInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profilePhotoLabel: {
    fontSize: 'clamp(10px, 1vw, 14px)',
    fontWeight: 400,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 4,
  },
  addPhotoIcon: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    background: '#2563EB',
    color: '#fff',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    lineHeight: 1,
    border: '2px solid #fff',
    cursor: 'pointer',
    padding: 0,
  },
  plusNudge: {
    position: 'relative',
    top: '-1px',
    left: '0px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(12px, 2vw, 24px)',
    width: '100%',
    margin: '0 auto clamp(16px, 3vw, 32px) auto',
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '24px 16px',
    },
  },
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  inputContainer: {
    width: '100%',
    '@media (min-width: 1024px)': {
      width: 'calc(50% - 8px)',
      ':last-child:nth-child(odd)': {
        width: '100%',
      },
    },
  },
  error: {
    color: '#DC2626',
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
})

export default OnboardingStepTruckingInfo
